import classNames from 'classnames';
import { debounce } from 'lodash';
import React, { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { formatGeometry, formatPosition } from '/@/core/fields';
import { trackEvent as trackEventAction } from '/@/store/tracking/actions';
import type { Field } from '/@/types/field';
import { isMultiPolygon, isPoint } from '/@/utils';
import { markerMapIcons } from '.';
import DeviceLocation from './device-location';
import Geocoder from './geocoder';
import styles from './styles.module.css';
import { useMap } from './use-map';

type NewFieldMapProps = {
    setField: (field: Field) => void;
    mode: 'point' | 'polygon' | 'circle';
    country: string;
    zoomPosition: 'TOP_RIGHT' | 'BOTTOM_RIGHT';
    zoomControls: boolean;
    disabled: boolean;
    field: Field;
    fields: Field[];
};

const NewFieldMap = forwardRef(
    (
        {
            setField,
            mode,
            country,
            zoomPosition = 'TOP_RIGHT',
            zoomControls = true,
            disabled,
            field,
            fields = [],
        }: NewFieldMapProps,
        ref
    ) => {
        const dispatch = useDispatch();
        const [query, setQuery] = useState('');
        const [searchFocus, setSearchFocus] = useState(false);
        const [circle, setCircle] = useState(undefined);
        const mapRef = useRef(null);
        const markersRefs = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map());
        const polygonRefs = useRef<Map<string, google.maps.Polygon>>(new Map());
        const map = useMap({
            mapRef,
            ref,
            zoomControls,
            zoomPosition,
        });

        const trackEvent = bindActionCreators(trackEventAction, dispatch);

        // Helper function to create marker content for AdvancedMarkerElement
        const createMarkerContent = (iconType = 'point') => {
            const element = document.createElement('div');
            element.style.width = '35px';
            element.style.height = '35px';
            element.style.cursor = 'pointer';

            if (typeof markerMapIcons[iconType]?.url === 'function') {
                // For point markers, render the React component
                ReactDOM.render(React.createElement(markerMapIcons[iconType].url), element);
            } else {
                // For other markers, use background image
                element.style.backgroundImage = `url(${markerMapIcons[iconType]?.url as string})`;
                element.style.backgroundSize = 'contain';
                element.style.backgroundRepeat = 'no-repeat';
                element.style.backgroundPosition = 'center';
            }

            return element;
        };

        useEffect(() => {
            if (!map) return;
            if (mode !== 'circle') return;

            const listeners = [];
            const createPolygonFromCircle = () => {
                const points = [];
                const radius = circle.getRadius();
                for (let i = 0; i < 360; i += 10) {
                    const angle = (i * Math.PI) / 180;
                    const dx = radius * Math.cos(angle);
                    const dy = radius * Math.sin(angle);
                    const point = new google.maps.LatLng(
                        (circle?.getCenter()?.lat() || 0) + dy / 111111,
                        (circle?.getCenter()?.lng() || 0) +
                            dx / (111111 * Math.cos((circle?.getCenter()?.lat() ?? 0 * Math.PI) / 180))
                    );
                    points.push(point);
                }
                setField((prev) => ({ ...prev, geometry: [points.map((pos) => [pos.lng(), pos.lat()])] }));
            };

            function calculateRadius(map) {
                const bounds = map.getBounds();
                const center = bounds.getCenter();
                const ne = bounds.getNorthEast();
                const radiusMeters = google.maps.geometry.spherical.computeDistanceBetween(center, ne) / 4;
                return radiusMeters;
            }

            const circle = new google.maps.Circle({
                map,
                strokeColor: '#FFFFFF',
                strokeWeight: 2,
                editable: true,
                clickable: true,
                draggable: true,
                center: map.getCenter(),
                radius: calculateRadius(map),
                opacity: disabled ? 0.6 : undefined,
            });
            setCircle(circle);

            ['dragend', 'radius_changed'].forEach((event) => {
                listeners.push(google.maps.event.addListener(circle, event, createPolygonFromCircle));
            });
            createPolygonFromCircle();
            return () => {
                circle.setMap(null);
            };
        }, [map, mode]);

        useEffect(() => {
            if (!map) return;
            if (mode !== 'polygon') return;
            const listeners = [];
            const polygon = new google.maps.Polygon({
                map,
                strokeColor: '#FFFFFF',
                strokeWeight: 2,
                editable: true,
                clickable: true,
                draggable: true,
                paths: [(field?.geometry || []).map(([lng, lat]) => ({ lng, lat }))],
                opacity: disabled ? 0.6 : undefined,
            });
            const getPath = debounce(() => {
                const arr = polygon.getPath().getArray();
                setField((prev) => ({
                    ...prev,
                    geometry: [arr.map((pos) => [pos.lng(), pos.lat()])],
                }));
            }, 500);

            ['insert_at', 'remove_at', 'set_at'].forEach((event) => {
                listeners.push(google.maps.event.addListener(polygon.getPath(), event, getPath));
            });

            listeners.push(
                map.addListener('click', (event) => {
                    const lng = event.latLng.lng();
                    const lat = event.latLng.lat();
                    const path = polygon.getPath();
                    path.push(new google.maps.LatLng({ lat, lng }));
                    polygon.setPaths([path]);
                })
            );
            return () => {
                polygon.setMap(null);
                listeners.forEach((listener) => google.maps.event.removeListener(listener));
            };
        }, [map, mode, disabled]);

        useEffect(() => {
            if (!map) return;
            if (mode !== 'point') return;
            const listeners = [];
            const [lng, lat] = (field?.geometry || []).length ? field.geometry[0] : [];
            const position = lng ? new google.maps.LatLng({ lng, lat }) : undefined;
            const marker = new google.maps.marker.AdvancedMarkerElement({
                map,
                content: createMarkerContent('point'),
                draggable: true,
                position,
            });
            const debouncedSetField = debounce(({ lng, lat }) => {
                setField((prev) => ({ ...prev, geometry: [[lng, lat]] }));
            }, 500);
            listeners.push(
                google.maps.event.addListener(marker, 'dragend', () => {
                    const { lat, lng } = marker.position.toJSON();
                    debouncedSetField({ lng, lat });
                })
            );
            listeners.push(
                map.addListener('click', (event) => {
                    const { lng, lat } = event.latLng.toJSON();
                    marker.position = event.latLng;
                    debouncedSetField({ lng, lat });
                })
            );
            return () => {
                marker.map = null;
                listeners.forEach((listener) => google.maps.event.removeListener(listener));
            };
        }, [map, mode, disabled]);

        useEffect(() => {
            return () => {
                if (map) google.maps.event.clearInstanceListeners(map);
            };
        }, []);

        useEffect(() => {
            if (!map) return;

            fields.forEach((field) => {
                const fieldId = String(field.id);
                if (!markersRefs.current.has(fieldId)) {
                    const marker = new google.maps.marker.AdvancedMarkerElement({
                        position: { lng: field.lng, lat: field.lat },
                        map,
                        content: createMarkerContent((field as any)?.iconType || 'point'),
                        title: field.name,
                    });

                    markersRefs.current.set(fieldId, marker);
                }

                if (!isPoint(field.geometry)) {
                    // Handle MultiPolygon by creating separate polygons for each polygon
                    const geometryPolygons = isMultiPolygon(field.geometry)
                        ? field.geometry.map((geometry, index) => ({
                              geometry: geometry,
                              polygonId: `${fieldId}-${index}`,
                          }))
                        : [
                              {
                                  geometry: field.geometry,
                                  polygonId: fieldId,
                              },
                          ];

                    geometryPolygons.forEach(({ geometry, polygonId }) => {
                        if (!polygonRefs.current.has(polygonId)) {
                            // Format geometry using formatGeometry which handles all cases recursively
                            const paths = formatGeometry(geometry);

                            // Create polygon with paths from the start to ensure holes are rendered correctly
                            const polygon = new google.maps.Polygon({
                                map,
                                strokeColor: '#FFFFFF',
                                strokeWeight: 1,
                                paths: paths,
                                fillOpacity: 0.2,
                                strokeOpacity: 0.6,
                                clickable: false,
                            });
                            polygonRefs.current.set(polygonId, polygon);
                        }
                    });
                }
            });

            [...polygonRefs.current.values()].forEach((el) => {
                if (el.map) return;
                el.setMap(map);
            });
        }, [fields, map]);

        const setCenter = (position) => {
            if (!map) return;
            map.setCenter(formatPosition(position));
            map.setZoom(14);
        };
        const handleSearchChange = (value) => {
            setQuery(value);
            if (!value) setSearchFocus(false);
        };
        const handlePositionResult = (position) => setCenter(position);
        const handleSearchFocus = () => setSearchFocus(true);
        const handleSearchBlur = () => setSearchFocus(false);
        const handleSearchReset = (event) => {
            event.stopPropagation();
            setQuery('');
            setSearchFocus(false);
        };

        return (
            <Fragment>
                <div className={classNames(styles.root)} ref={mapRef} />
                <div className={styles.topTools}>
                    <div className={styles.flexHorizontalGrow}>
                        <Geocoder
                            query={query}
                            country={country}
                            onResult={handlePositionResult}
                            onFocus={handleSearchFocus}
                            onBlur={handleSearchBlur}
                            onChange={handleSearchChange}
                            onReset={handleSearchReset}
                            trackEvent={trackEvent}
                        />
                        {!searchFocus && !query && (
                            <DeviceLocation onResult={handlePositionResult} trackEvent={trackEvent} />
                        )}
                    </div>
                </div>
            </Fragment>
        );
    }
);

export default NewFieldMap;
